// FILE: src/components/modals/FavoritesModal.jsx

import React from 'react';
import { Heart, Plus, Home, Sparkles, TrendingUp, BadgePercent, Tag, Gift } from 'lucide-react';
import { getProductImage } from '../../utils/imageHelpers';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getDiscountAmount = (product) => {
  const regularPrice = toNumber(product?.price);
  const salePrice = toNumber(product?.sale_price);

  if (salePrice > 0 && regularPrice > salePrice) {
    return Math.round(regularPrice - salePrice);
  }

  return 0;
};

const getEffectivePrice = (product) => {
  const regularPrice = toNumber(product?.price);
  const salePrice = toNumber(product?.sale_price);

  if (salePrice > 0 && regularPrice > 0 && salePrice < regularPrice) {
    return salePrice;
  }

  return regularPrice;
};

const getDiscountPercent = (product) => {
  const regularPrice = toNumber(product?.price);
  const salePrice = toNumber(product?.sale_price);

  if (salePrice > 0 && regularPrice > salePrice) {
    return Math.round(((regularPrice - salePrice) / regularPrice) * 100);
  }

  return 0;
};

const getFallbackUpscaleMessage = (product) => {
  const effectivePrice = getEffectivePrice(product);

  if (effectivePrice >= 250) {
    return 'Premium upgrade: combine with a weekly subscription and save 12%.';
  }

  if (effectivePrice >= 120) {
    return 'Family value offer: add 2 units and unlock delivery savings.';
  }

  return 'Smart saver offer: add 3 units from this line and save 8%.';
};

const getPromotionMessage = (product) => {
  const backendMessage = [
    product?.promotion_message,
    product?.promotionMessage,
    product?.promo_message,
    product?.promoMessage,
    product?.offer_message,
    product?.offerMessage,
    product?.marketing_message,
    product?.marketingMessage,
  ].find((value) => typeof value === 'string' && value.trim().length > 0);

  if (backendMessage) {
    return backendMessage.trim();
  }

  return getFallbackUpscaleMessage(product);
};

const FavoritesModal = ({
  isOpen,
  onClose,
  favorites,
  setFavorites,
  toggleFavorite,
  products = [],
  addToCart,
  showToast,
}) => {
  if (!isOpen) return null;

  const favoritesList = Array.isArray(favorites) ? favorites : [];
  const favoriteIdSet = new Set(
    favoritesList
      .map((item) => (item?.id === undefined || item?.id === null ? '' : String(item.id)))
      .filter(Boolean)
  );

  const promotionSuggestions = Array.isArray(products)
    ? products
      .filter((item) => item && item.id !== undefined && item.id !== null)
      .filter((item) => !favoriteIdSet.has(String(item.id)))
      .sort((left, right) => getDiscountAmount(right) - getDiscountAmount(left))
      .slice(0, 3)
    : [];

  const removeFavorite = (product) => {
    if (typeof toggleFavorite === 'function') {
      toggleFavorite(product);
      return;
    }

    if (typeof setFavorites === 'function') {
      setFavorites(favoritesList.filter((item) => String(item.id) !== String(product.id)));
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    showToast(`${product.name} added to cart!`, 'success');
  };

  return (
    <div className="min-h-full bg-gray-50">
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-b-3xl">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            title="Go back"
          >
            <Home size={22} />
          </button>
          {favoritesList.length > 0 && (
            <span className="bg-white bg-opacity-20 text-white text-xs font-semibold px-3 py-1 rounded-full">
              {favoritesList.length} saved
            </span>
          )}
        </div>
        <h1 className="text-2xl font-bold">My Favorites</h1>
        <p className="text-blue-100">Your saved products in one place</p>
      </div>

      <div className="p-4">

        {favoritesList.length === 0 ? (
          <div className="text-center py-12">
            <Heart size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">No favorites yet</p>
            <p className="text-gray-400 text-sm mt-2">Heart products you love to save them here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {favoritesList.map((product) => (
              <div 
                key={product.id} 
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 border-b border-pink-100">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="bg-white rounded-xl p-3 shadow-sm flex items-center justify-center min-w-[72px]">
                        {getProductImage(product, 'large')}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-800 truncate">{product.name}</h3>
                        {product.description && (
                          <p className="text-xs text-gray-500 truncate">{product.description}</p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-lg font-bold text-blue-600">₹{product.price}</span>
                          {product.fat && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                              {product.fat} fat
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => removeFavorite(product)}
                      className="text-red-500 hover:bg-red-50 p-2 rounded-full transition-all"
                      title="Remove from favorites"
                    >
                      <Heart size={20} fill="currentColor" />
                    </button>
                  </div>
                </div>

                <div className="p-4">
                  <div className="mb-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 px-3 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700">
                        <BadgePercent size={14} />
                        Deal Booster
                      </div>
                      {getDiscountPercent(product) > 0 && (
                        <span className="text-[10px] font-bold bg-red-500 text-white px-2 py-0.5 rounded-full shadow-sm ring-1 ring-red-300 motion-safe:animate-pulse">
                          {getDiscountPercent(product)}% OFF
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-semibold text-rose-700 mt-1.5 flex items-center gap-1.5">
                      <Tag size={13} />
                      {getDiscountAmount(product) > 0
                        ? `Flash price drop: Save ₹${getDiscountAmount(product)} on ${product.name}.`
                        : `Combo saver: Pair ${product.name} with daily essentials for extra value.`}
                    </p>
                    <p className="text-xs text-amber-800 mt-1">{getPromotionMessage(product)}</p>
                  </div>

                  <button
                    onClick={() => handleAddToCart(product)}
                    className="w-full bg-blue-500 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-600 transition-all flex items-center justify-center gap-2"
                  >
                    <Plus size={16} />
                    Add Deal to Cart
                  </button>
                </div>
                    </div>
            ))}
          </div>
        )}

        {favoritesList.length > 0 && (
          <div className="mt-6 rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 via-blue-50 to-cyan-50 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-indigo-600" />
                <h3 className="text-sm font-semibold text-indigo-900">Upscale Promotions</h3>
              </div>
              <span className="text-[11px] font-semibold text-indigo-700 bg-white px-2 py-1 rounded-full border border-indigo-100 inline-flex items-center gap-1">
                <BadgePercent size={12} />
                Limited
              </span>
            </div>

            <div className="space-y-2">
              {favoritesList.slice(0, 3).map((product) => (
                <div key={`promo-${product.id}`} className="bg-white/80 rounded-lg p-3 border border-indigo-100">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{product.name}</p>
                      <p className="text-[11px] text-rose-700 mt-1 inline-flex items-center gap-1.5 font-semibold">
                        <Gift size={12} />
                        {getDiscountAmount(product) > 0
                          ? `Save ₹${getDiscountAmount(product)} with this favorite deal`
                          : 'Add now to unlock bundle-only savings'}
                      </p>
                      <p className="text-xs text-indigo-700 mt-1">{getPromotionMessage(product)}</p>
                    </div>
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="flex-shrink-0 text-xs font-semibold text-indigo-700 bg-indigo-100 hover:bg-indigo-200 px-3 py-1.5 rounded-full transition-colors"
                    >
                      Add Deal
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {promotionSuggestions.length > 0 && (
              <div className="mt-4 border-t border-indigo-200 pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp size={15} className="text-cyan-700" />
                  <p className="text-xs font-semibold text-cyan-800">Recommended add-ons</p>
                </div>
                <div className="space-y-2">
                  {promotionSuggestions.map((product) => (
                    <div key={`addon-${product.id}`} className="bg-white rounded-lg border border-cyan-100 px-3 py-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{product.name}</p>
                        <p className="text-xs text-gray-500 inline-flex items-center gap-1.5">
                          <Tag size={12} className="text-cyan-700" />
                          {getDiscountAmount(product) > 0
                            ? `Save up to ₹${getDiscountAmount(product)} with this combo`
                            : 'Bundle suggestion for your current favorites'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleAddToCart(product)}
                        className="text-xs font-semibold text-cyan-700 bg-cyan-100 hover:bg-cyan-200 px-3 py-1.5 rounded-full transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {favoritesList.length > 0 && (
          <div className="mt-6 bg-pink-50 border border-pink-200 rounded-xl p-4">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Tip:</span> Add your favorite items to cart for quick reordering.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesModal;